/**
 * The ONE analytics module (ANALYTICS.md §1): `track`, `optIn`, `optOut`, `identifyAccount`. Platform-neutral: it talks
 * to an injected `AnalyticsDriver` (the per-app PostHog wrapper), so nothing else in the code imports PostHog.
 *
 * Privacy rules enforced here: nothing is sent until the player opts in (consent persisted locally; `unknown` and
 * `out` both mean off); every event carries `app='handegg'` + the common properties; string values that are not
 * short enum-like tokens are rejected (no free text); `identify` is never called unless `identifyAccount` is, and
 * nothing calls it before accounts exist (Phase 2).
 */
import { signal } from '@preact/signals-core';
import { getPlatform, type KeyValueStorage } from '../platform';
import type {
  AnalyticsDriver,
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsProps,
  AnalyticsSource,
  AnalyticsValue,
  ConsentState,
} from './events';

export const ANALYTICS_KEY = 'hf.analytics.v1';
export const APP_TAG = 'handegg';
export const MAX_STRING_LEN = 40;
/** Enum-like tokens only: letters, digits, `_ - . :` (no spaces, so no sentences / names with spaces). */
const TOKEN_RE = /^[A-Za-z0-9_.:-]+$/;

export interface AnalyticsLocalState {
  consent: ConsentState;
  /** Epoch ms of the first launch (local only). */
  installedAt: number;
  firstOpenSent: boolean;
  /** Lifetime count of started matches (local counter, not an id). */
  matchCount: number;
  /** A match in progress: if still here on next launch the app was killed. */
  activeMatch: { mode: string; startedAt: number; lastSeenAt: number } | null;
  installCampaign: string;
}

export interface AnalyticsEnv {
  driver: AnalyticsDriver | null;
  platform: 'ios' | 'android' | 'web';
  appVersion: string;
  buildNumber: string;
  otaUpdateId: string;
  /** Dev builds tag app_version with a `-dev` suffix so dashboards can filter them out. */
  dev: boolean;
  storage?: KeyValueStorage | null;
  now?: () => number;
}

const fresh = (now: number): AnalyticsLocalState => ({
  consent: 'unknown',
  installedAt: now,
  firstOpenSent: false,
  matchCount: 0,
  activeMatch: null,
  installCampaign: 'none',
});

let env: AnalyticsEnv | null = null;
let state: AnalyticsLocalState = fresh(0);
let storage: KeyValueStorage | null = null;
let accountState: 'none' | 'guest' | 'linked' = 'none';
const listeners = new Set<() => void>();
const nowMs = (): number => (env?.now ? env.now() : Date.now());

const persist = (): void => {
  try {
    storage?.setItem(ANALYTICS_KEY, JSON.stringify(state));
  } catch {
    /* storage blocked: kept in memory */
  }
};
/** Reactive mirror of the consent state (UI: first-run prompt, Settings toggle). */
export const consentSignal = signal<ConsentState>('unknown');
const emitChange = (): void => {
  consentSignal.value = state.consent;
  listeners.forEach((l) => l());
};

const load = (): AnalyticsLocalState => {
  const base = fresh(nowMs());
  try {
    const raw = storage?.getItem(ANALYTICS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<AnalyticsLocalState>;
      return {
        ...base,
        ...p,
        consent: p.consent === 'in' || p.consent === 'out' ? p.consent : 'unknown',
        installedAt: Number(p.installedAt) > 0 ? Number(p.installedAt) : base.installedAt,
      };
    }
  } catch {
    /* corrupt: start fresh */
  }
  return base;
};

/** Super-properties (§1). */
export const commonProps = (): Record<string, AnalyticsValue> => ({
  app: APP_TAG,
  platform: env?.platform ?? 'web',
  app_version: env ? (env.dev ? `${env.appVersion}-dev` : env.appVersion) : 'unknown',
  build_number: env?.buildNumber ?? 'unknown',
  ota_update_id: env?.otaUpdateId ?? 'embedded',
  install_campaign: state.installCampaign,
  account_state: accountState,
});

/** Validate props: numbers/booleans pass; strings must be short enum-like tokens. */
export const validProps = (props: AnalyticsProps): boolean => {
  for (const v of Object.values(props)) {
    if (v === undefined || typeof v === 'boolean') continue;
    if (typeof v === 'number') {
      if (!Number.isFinite(v)) return false;
      continue;
    }
    if (typeof v !== 'string') return false;
    if (v.length > MAX_STRING_LEN || !TOKEN_RE.test(v)) return false;
  }
  return true;
};

/**
 * Initialise (once per launch, from the app shell). Creates the driver OFF; if the player had opted in on a previous
 * launch it is switched on and the launch events fire. Never sends anything for `unknown` / `out`.
 */
export const initAnalytics = (e: AnalyticsEnv, campaignFromLaunch: string | null = null): void => {
  env = e;
  storage = e.storage === undefined ? getPlatform().storage() : e.storage;
  accountState = 'none';
  state = load();
  const token = campaignFromLaunch && validCampaign(campaignFromLaunch) ? campaignFromLaunch : null;
  if (token && state.installCampaign === 'none') state.installCampaign = token;
  persist();
  consentSignal.value = state.consent;
  try {
    e.driver?.init();
    e.driver?.register(commonProps());
    if (state.consent === 'in') e.driver?.optIn();
    else e.driver?.optOut();
  } catch {
    /* analytics never breaks the app */
  }
  if (state.consent === 'in') launchEvents(true);
};

/** Late campaign token (native deferred link): sets `install_campaign` once, only while it is still `none`. */
export const setInstallCampaign = (token: string): void => {
  if (state.installCampaign !== 'none' || !validCampaign(token)) return;
  state.installCampaign = token;
  persist();
  try {
    env?.driver?.register({ install_campaign: token });
  } catch {
    /* ignore */
  }
};

const validCampaign = (t: string): boolean => t.length <= MAX_STRING_LEN && TOKEN_RE.test(t);

export const isOptedIn = (): boolean => state.consent === 'in';
export const consentState = (): ConsentState => state.consent;
/** True until the player has answered the first-run prompt. */
export const needsConsentPrompt = (): boolean => state.consent === 'unknown';
export const onAnalyticsChange = (fn: () => void): (() => void) => (
  listeners.add(fn),
  () => void listeners.delete(fn)
);
export const localState = (): Readonly<AnalyticsLocalState> => state;

/** Capture an event. Returns false (and sends nothing) when off, un-initialised or the props are not allowed. */
export const track = <E extends AnalyticsEventName>(event: E, props?: AnalyticsEventMap[E]): boolean => {
  if (!env?.driver || state.consent !== 'in') return false;
  const p = (props ?? {}) as AnalyticsProps;
  if (!validProps(p)) {
    if (env.dev) getPlatform().log.warn(`[analytics] ${event}: rejected property values`);
    return false;
  }
  const clean: Record<string, AnalyticsValue> = { ...commonProps() };
  for (const [k, v] of Object.entries(p)) if (v !== undefined) clean[k] = v;
  clean['app'] = APP_TAG;
  try {
    env.driver.capture(event, clean);
  } catch {
    return false;
  }
  return true;
};

const daysSinceInstall = (): number => Math.max(0, Math.floor((nowMs() - state.installedAt) / 86_400_000));

/** `first_open` (once per install) + `app_opened`. */
const launchEvents = (cold: boolean): void => {
  if (!state.firstOpenSent) {
    state.firstOpenSent = true;
    persist();
    track('first_open', {});
  }
  track('app_opened', { is_cold_start: cold, days_since_install: daysSinceInstall() });
};

/** Player opted in (first-run prompt or Settings). */
export const optIn = (source: AnalyticsSource): void => {
  const was = state.consent;
  state.consent = 'in';
  persist();
  try {
    env?.driver?.optIn();
  } catch {
    /* ignore */
  }
  emitChange();
  if (was === 'in') return;
  track('analytics_opted_in', { source });
  launchEvents(true);
};

/**
 * Player opted out. `analytics_opted_out` is sent only if they were opted in (it is the last event, sent before the
 * SDK is switched off); declining the first-run prompt sends nothing at all.
 */
export const optOut = (source: AnalyticsSource): void => {
  const was = state.consent;
  if (was === 'in') track('analytics_opted_out', { source });
  state.consent = 'out';
  persist();
  try {
    env?.driver?.optOut();
  } catch {
    /* ignore */
  }
  emitChange();
};

/** Salt for `opaqueAccountId` (not a secret: only keeps raw user ids out of PostHog). */
const OPAQUE_SALT = 'handegg.analytics.v1';

/**
 * Opaque PostHog distinct-id from a server user id: FNV-1a over `salt:userId`, hex, prefixed `a_`. Never pass
 * email / Apple / Google subject ids here — only the server's `user_id`.
 */
export const opaqueAccountId = (userId: string): string => {
  let h = 0xcbf29ce484222325n;
  const s = `${OPAQUE_SALT}:${userId}`;
  for (let i = 0; i < s.length; i++) {
    h ^= BigInt(s.charCodeAt(i));
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return `a_${h.toString(16)}`;
};

/** Super-property `account_state` (Phase 2). `guest` once an account exists; `linked` after Apple/Google/email. */
export const setAccountState = (next: 'none' | 'guest' | 'linked'): void => {
  // Never downgrade linked → guest (syncWallet may call guest repeatedly after a link).
  if (accountState === next) return;
  if (accountState === 'linked' && next === 'guest') return;
  accountState = next;
  try {
    env?.driver?.register({ account_state: next });
  } catch {
    /* ignore */
  }
};

/**
 * Phase 2 (accounts): link the anonymous id to a salted hash / opaque id. Needs consent. Prefer
 * `opaqueAccountId(userId)` — never email, name, or Apple/Google subject ids.
 */
export const identifyAccount = (opaqueId: string): void => {
  if (state.consent !== 'in' || !env?.driver || !opaqueId) return;
  accountState = 'linked';
  try {
    env.driver.register({ account_state: 'linked' });
    env.driver.identify(opaqueId);
  } catch {
    /* ignore */
  }
};

/** Fire `ota_update_applied` once when this launch is running a different update id than the last one we noted. */
export const noteOtaUpdateApplied = (fromUpdateId: string, toUpdateId: string): boolean => {
  if (!fromUpdateId || !toUpdateId || fromUpdateId === toUpdateId) return false;
  return track('ota_update_applied', { from_update_id: fromUpdateId, to_update_id: toUpdateId });
};

/** Local match counter (works while opted out; never leaves the device). */
export const nextMatchIndex = (): number => {
  state.matchCount += 1;
  persist();
  return state.matchCount;
};
export const currentMatchIndex = (): number => state.matchCount;

export const setActiveMatch = (m: AnalyticsLocalState['activeMatch']): void => {
  state.activeMatch = m;
  persist();
};
export const activeMatch = (): AnalyticsLocalState['activeMatch'] => state.activeMatch;

/** App returned to the foreground (not a cold start). */
export const trackResume = (): void => {
  if (state.consent === 'in')
    track('app_opened', { is_cold_start: false, days_since_install: daysSinceInstall() });
};

/** Test helper. */
export const resetAnalyticsForTests = (): void => {
  env = null;
  state = fresh(0);
  storage = null;
  accountState = 'none';
  consentSignal.value = 'unknown';
  listeners.clear();
};
